/**
 * @module useErrorTracking
 * @description Comprehensive error tracking hooks for WMS DAOUXUN
 * Inspired by supermemory's error-tracking patterns
 * 
 * Provides:
 * - API error tracking
 * - Component error tracking
 * - Validation error tracking
 * - Global error boundary support
 * - User interaction tracking
 */

import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useCurrentUser } from './useAuth';

/**
 * Hook for comprehensive error tracking
 * Tracks errors with context including user, pathname, and timestamps
 * 
 * @returns {Object} Error tracking functions
 */
export const useErrorTracking = () => {
  const { data: user } = useCurrentUser();
  const location = useLocation();
  const pathname = location?.pathname || '/';

  /**
   * Track a general error with context
   * @param {Error|unknown} error - The error to track
   * @param {Object} context - Additional context for the error
   */
  const trackError = useCallback((error, context = {}) => {
    const errorDetails = {
      error_message: error instanceof Error ? error.message : String(error),
      error_stack: error instanceof Error ? error.stack : undefined,
      error_name: error instanceof Error ? error.name : 'Unknown',
      pathname,
      user_id: user?.id,
      user_email: user?.email,
      user_role: user?.role,
      timestamp: new Date().toISOString(),
      ...context,
    };

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('[WMS Error Tracking]', errorDetails);
    }

    // Send to backend analytics endpoint
    try {
      navigator.sendBeacon?.(
        '/api/v1/analytics/errors',
        JSON.stringify(errorDetails)
      );
    } catch {
      // Silently fail if beacon not available
    }

    return errorDetails;
  }, [pathname, user]);

  /**
   * Track an API error with endpoint context
   * @param {Error|unknown} error - The error to track
   * @param {string} endpoint - The API endpoint that failed
   * @param {string} method - The HTTP method used
   */
  const trackApiError = useCallback((error, endpoint, method) => {
    return trackError(error, {
      error_type: 'api_error',
      api_endpoint: endpoint,
      api_method: method,
    });
  }, [trackError]);

  /**
   * Track a component error
   * @param {Error|unknown} error - The error to track
   * @param {string} componentName - The name of the component that errored
   */
  const trackComponentError = useCallback((error, componentName) => {
    return trackError(error, {
      error_type: 'component_error',
      component_name: componentName,
    });
  }, [trackError]);

  /**
   * Track a validation error
   * @param {Error|unknown} error - The error to track
   * @param {string} formName - The name of the form
   * @param {string} field - The field that failed validation
   */
  const trackValidationError = useCallback((error, formName, field) => {
    return trackError(error, {
      error_type: 'validation_error',
      form_name: formName,
      field_name: field,
    });
  }, [trackError]);

  /**
   * Track a network error
   * @param {Error|unknown} error - The error to track
   * @param {string} url - The URL that failed
   */
  const trackNetworkError = useCallback((error, url) => {
    return trackError(error, {
      error_type: 'network_error',
      failed_url: url,
    });
  }, [trackError]);

  return {
    trackError,
    trackApiError,
    trackComponentError,
    trackValidationError,
    trackNetworkError,
  };
};

/**
 * Hook for tracking user interactions
 * Useful for analytics and debugging user flows
 * 
 * @returns {Object} Interaction tracking functions
 */
export const useInteractionTracking = () => {
  const { data: user } = useCurrentUser();
  const location = useLocation();
  const pathname = location?.pathname || '/';

  /**
   * Track a user interaction
   * @param {string} action - The action performed
   * @param {Object} details - Additional details about the interaction
   */
  const trackInteraction = useCallback((action, details = {}) => {
    const interactionData = {
      action,
      pathname,
      user_id: user?.id,
      user_role: user?.role,
      timestamp: new Date().toISOString(),
      ...details,
    };

    // Log in development (disabled for production builds)
    // eslint-disable-next-line no-console
    if (process.env.NODE_ENV === 'development') console.debug('[WMS Interaction]', interactionData);

    // Send to analytics
    try {
      navigator.sendBeacon?.(
        '/api/v1/analytics/interactions',
        JSON.stringify(interactionData)
      );
    } catch {
      // Silently fail
    }

    return interactionData;
  }, [pathname, user]);

  /**
   * Track a form submission
   * @param {string} formName - The name of the form
   * @param {boolean} success - Whether the submission was successful
   * @param {Object} details - Additional details
   */
  const trackFormSubmission = useCallback((formName, success, details = {}) => {
    return trackInteraction('form_submission', {
      form_name: formName,
      success,
      ...details,
    });
  }, [trackInteraction]);

  /**
   * Track a button click
   * @param {string} buttonName - The name/identifier of the button
   * @param {string} context - The context where the button was clicked
   */
  const trackButtonClick = useCallback((buttonName, context) => {
    return trackInteraction('button_click', {
      button_name: buttonName,
      context,
    });
  }, [trackInteraction]);

  /**
   * Track a link click
   * @param {string} url - The URL of the link
   * @param {string} linkText - The text of the link
   * @param {boolean} external - Whether the link is external
   */
  const trackLinkClick = useCallback((url, linkText, external = false) => {
    return trackInteraction('link_click', {
      url,
      link_text: linkText,
      external,
    });
  }, [trackInteraction]);

  /**
   * Track a modal open event
   * @param {string} modalName - The name of the modal
   */
  const trackModalOpen = useCallback((modalName) => {
    return trackInteraction('modal_open', {
      modal_name: modalName,
    });
  }, [trackInteraction]);

  /**
   * Track a modal close event
   * @param {string} modalName - The name of the modal
   */
  const trackModalClose = useCallback((modalName) => {
    return trackInteraction('modal_close', {
      modal_name: modalName,
    });
  }, [trackInteraction]);

  /**
   * Track a tab change
   * @param {string} fromTab - The tab being left
   * @param {string} toTab - The tab being navigated to
   */
  const trackTabChange = useCallback((fromTab, toTab) => {
    return trackInteraction('tab_change', {
      from_tab: fromTab,
      to_tab: toTab,
    });
  }, [trackInteraction]);

  /**
   * Track a search action
   * @param {string} query - The search query
   * @param {number} resultsCount - Number of results returned
   */
  const trackSearch = useCallback((query, resultsCount) => {
    return trackInteraction('search', {
      query_length: query?.length || 0,
      results_count: resultsCount,
    });
  }, [trackInteraction]);

  /**
   * Track a filter change
   * @param {string} filterName - The name of the filter
   * @param {any} filterValue - The new filter value
   */
  const trackFilterChange = useCallback((filterName, filterValue) => {
    return trackInteraction('filter_change', {
      filter_name: filterName,
      filter_value: String(filterValue),
    });
  }, [trackInteraction]);

  return {
    trackInteraction,
    trackFormSubmission,
    trackButtonClick,
    trackLinkClick,
    trackModalOpen,
    trackModalClose,
    trackTabChange,
    trackSearch,
    trackFilterChange,
  };
};

/**
 * Global error handler setup hook
 * Sets up window-level error handlers for unhandled errors and rejections
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether to enable global error handling
 */
export const useGlobalErrorHandler = (options = { enabled: true }) => {
  const { trackError } = useErrorTracking();

  useEffect(() => {
    if (!options.enabled) return;

    const handleError = (event) => {
      trackError(event.error || event.message, {
        error_type: 'global_error',
        source: 'window_error',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    };

    const handleUnhandledRejection = (event) => {
      trackError(event.reason, {
        error_type: 'unhandled_promise_rejection',
        source: 'promise_rejection',
      });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [options.enabled, trackError]);
};

export default useErrorTracking;
